import { ChangeEvent, FC, MouseEvent, useEffect, useState } from 'react';

import { Buttons, Form, FormElements } from '../../../common/AddEditFormLayout/AddEditFormLayout.styles';
import {
  DropdownWrapperMedium,
  InputWrapperLarge,
  InputWrapperMedium
} from '../../../common/FormElementWrappers/FormElementWrappers.styles';
import Header from '../../../common/Header/Header';
import { H2 } from '../../../common/Headings/Headings.styles';
import Button from '../../../components/Button/Button';
import Dropdown from '../../../components/Dropdown/Dropdown';
import Input from '../../../components/Input/Input';
import RadioGroup from '../../../components/RadioGroup/RadioGroup';
import { RadioGroupLayouts } from '../../../components/RadioGroup/RadioGroup.types';
import { RadioWrapper, RowWrapper, TextAreaWrapper } from './EditBase.styles';

import { NumberFormatValues, SourceInfo } from 'react-number-format';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import UnsavedChangesModal from '../../../common/UnsavedChangesModal/UnsavedChangesModal';
import MaskedInput from '../../../components/MaskedInput/MaskedInput';
import Textarea from '../../../components/Textarea/Textarea';
import { STATUS, StatusKeys } from '../../../constants/ApiEnums';
import { useBase, useEditBase } from '../../../services/base/base.service';
import { useFetchErrorToast } from '../../../utils/useFetchErrorToasts/useFetchErrorToast';
import { useGenericErrorMessage } from '../../../utils/useGenericErrorMessage/useGenericErrorMessage';
import useNavigateWithToast from '../../../utils/useNavigateWithToast/useNavigateWithToast';
import {OPTIONS_PLACEHOLDER} from "../../../constants/Dropdown";
import { SubTitle } from '../../Classes/ClassForm/ClassForm.styles';
import { readOnlyLabelTableText } from '../../../constants/GlobalTexts';

const EditBase: FC = () => {
  const navigateWithToast = useNavigateWithToast();
  const queryClient = useQueryClient();
  const { baseNo } = useParams<{ baseNo: string }>();
  const _baseNo = baseNo as string;

  const { data, isError } = useBase({ baseNo: _baseNo }, true);
  const { mutate, isError: editBaseIsError, error: editBaseError } = useEditBase(_baseNo);

  useFetchErrorToast([{ isError, label: 'Error fetching base data', id: 'baseDataFetchError' }]);
  useGenericErrorMessage([{error: editBaseError, isError: editBaseIsError}]);

  const baseData = data?.data;

  const [formValues, setFormValues] = useState({
    displayInOe: false,
    validOnWeb: false,
    displayDesc: '',
    seqNo: '',
    synopsis: '',
    statusNo: '',
  });

  const [initialFormValues, setInitialFormValues] = useState({
    displayInOe: false,
    validOnWeb: false,
    displayDesc: '',
    seqNo: '',
    synopsis: '',
    statusNo: '',
  });

  const [formErrors, setFormErrors] = useState({
    displayDesc: '',
    seqNo: '',
    synopsis: '',
    statusNo: '',
  });

  const [unsavedChanges, setUnsavedChanges] = useState(false);

  const checkForUnsavedChanges = () => {
    if (
      initialFormValues.displayInOe === formValues.displayInOe &&
      initialFormValues.validOnWeb === formValues.validOnWeb &&
      initialFormValues.displayDesc === formValues.displayDesc &&
      initialFormValues.seqNo === formValues.seqNo &&
      initialFormValues.synopsis === formValues.synopsis &&
      initialFormValues.statusNo === formValues.statusNo
    ) {
      setUnsavedChanges(false);
    } else {
      setUnsavedChanges(true);
    }
  };

  useEffect(() => {
    checkForUnsavedChanges();
  }, [formValues]);
    
  useEffect(() => {
    if (baseData) {
      setFormValues({
        displayInOe: baseData.displayInOe,
        validOnWeb: baseData.validOnWeb,
        displayDesc: baseData.displayDesc,
        seqNo: baseData.seqNo.toString(),
        statusNo: baseData.statusNo.toString(),
        synopsis: baseData.synopsis
      });
      setInitialFormValues({
        displayInOe: baseData.displayInOe,
        validOnWeb: baseData.validOnWeb,
        displayDesc: baseData.displayDesc,
        seqNo: baseData.seqNo.toString(),
        statusNo: baseData.statusNo.toString(),
        synopsis: baseData.synopsis
      });
    }
  }, [baseData]);
  
  const handleDisplayInOeChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormValues({ ...formValues, displayInOe: event.target.id === 'YdisplayInOe' });
  };

  const handleValidonWebChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormValues({ ...formValues, validOnWeb: event.target.id === 'YvalidOnWeb' });
  };

  const handleBohDescChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFormErrors({
      ...formErrors,
      displayDesc: '',
    });
    setFormValues({ ...formValues, [event.target.id]: event.target.value });
  };

  const handleSynopsisChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setFormErrors({
      ...formErrors,
      synopsis: '',
    });
    setFormValues({ ...formValues, [event.target.id]: event.target.value });
  };

  const handleStatusDropdown = (event: ChangeEvent<HTMLSelectElement>) => {
    setFormValues({ ...formValues, [event.target.id]: event.target.value });
    setFormErrors && setFormErrors({ ...formErrors, [event.target.id]: '' });
  };

  const handleMaskedInputChange = (values: NumberFormatValues, sourceInfo: SourceInfo) => {
    if (sourceInfo.event) {
      setFormValues({ ...formValues, [sourceInfo.event.target.id]: values.value === '' ? '' : values.value });
      setFormErrors({ ...formErrors, [sourceInfo.event.target.id]: '' });
    }
  };

  const handleSave = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    
    let error = false;
    let newFormErrors = {
      displayDesc: '',
      seqNo: '',
      synopsis: '',
      statusNo: '',
    };
    
    if (formValues.displayDesc?.length > 9) {
      error = true;
      newFormErrors.displayDesc = 'BOH Description must be 9 characters or less';
    }
    if (!formValues.displayDesc || formValues.displayDesc.length < 1) {
      error = true;
      newFormErrors.displayDesc = 'Please enter a BOH Description';
    }
    if ([null, undefined, -1, ''].includes(formValues.seqNo)) {
      error = true;
      newFormErrors.seqNo = 'Sequence Number is required.';
    }
    if(formValues.seqNo?.length > 3) {
      error = true;
      newFormErrors.seqNo = 'Sequence Number must be 3 characters or less'
    }
    if (formValues.synopsis?.length > 255) {
      error = true;
      newFormErrors.synopsis = 'Synopsis must be 255 characters or less';
    }
    if (!error) {
      setUnsavedChanges(false);
      mutate(
        {
          baseNo: _baseNo,
          displayInOe: formValues.displayInOe,
          validOnWeb: formValues.validOnWeb,
          displayDesc: formValues.displayDesc,
          seqNo: +formValues.seqNo,
          synopsis: formValues.synopsis,
          statusNo: +formValues.statusNo
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries(['base', _baseNo]);
            navigateWithToast(`/tables/bases`, {
              label: 'Base has been updated successfully',
              id: 'baseUpdateSuccess',
            });
          },
        }
      );
    } else {
      setFormErrors(newFormErrors);
    }
  };

  return (
    <>
      <Header
        breadcrumb={{ label: 'Back to Base Table', to: '/tables/bases' }}
        title={`View Base ${baseData?.baseNo ? `- ${baseData?.baseNo}` : ''}`}
      />
      <Form>
        <FormElements>
          <H2>Base Table</H2>
          <RowWrapper>
            <InputWrapperMedium>
              <Input label='MOA Base Number' id='moaBaseNo' value={baseData?.baseNo?.toString()} disabled />
            </InputWrapperMedium>
            <InputWrapperMedium>
              <Input label='Base Number' id='baseNo' value={baseData?.baseNo?.toString()} disabled />
            </InputWrapperMedium>
          </RowWrapper>
          <RowWrapper>
            <InputWrapperMedium>
              <Input label='Short Description' id='description' value={baseData?.description} disabled/>
            </InputWrapperMedium>
            <InputWrapperLarge>
              <Input label='Long Description' id='longDescription' value={baseData?.longDescription} disabled/>
            </InputWrapperLarge>
          </RowWrapper>
          <TextAreaWrapper>
            <Textarea label='Synopsis' id='synopsis' value={formValues?.synopsis} onChange={handleSynopsisChange} errorText={formErrors.synopsis} />
          </TextAreaWrapper>
          <RowWrapper>
            <InputWrapperLarge>
            <Input label='BOH Description' id='displayDesc' value={formValues?.displayDesc} onChange={handleBohDescChange} errorText={formErrors.displayDesc} />
            </InputWrapperLarge>
            <DropdownWrapperMedium>
              <Dropdown
                label='Status'
                id='statusNo'
                options={Object.keys(STATUS).map((key: any) => {
                  return { value: key, label: `${key} - ${STATUS[key as StatusKeys]}` };
                })}
                value={formValues?.statusNo?.toString() || OPTIONS_PLACEHOLDER}
                errorText={formErrors?.statusNo}
                onChange={handleStatusDropdown}
              />
            </DropdownWrapperMedium>
          </RowWrapper>
          <RowWrapper>
            <InputWrapperMedium>
              <Input label='Mnemonic' id='mnemonic' value={baseData?.mnemonic} disabled />
            </InputWrapperMedium>
            <InputWrapperMedium>
              <MaskedInput
                label='Sequence Number'
                id='seqNo'
                value={formValues?.seqNo.toString()}
                errorText={formErrors?.seqNo}
                isAllowed={(values) => +values.value < 2147483648}
                onValueChange={handleMaskedInputChange}
              />
            </InputWrapperMedium>
            <RadioWrapper>
              <RadioGroup
                options={[
                  {
                    id: 'YvalidOnWeb',
                    label: 'Yes',
                  },
                  {
                    id: 'NvalidOnWeb',
                    label: 'No',
                  },
                ]}
                id='validOnWeb'
                label='Valid on Web?'
                layout={RadioGroupLayouts.Row}
                selected={baseData?.validOnWeb ? 'YvalidOnWeb' : 'NvalidOnWeb'}
                onChange={handleValidonWebChange}
              />
            </RadioWrapper>
            <RadioWrapper>
              <RadioGroup
                options={[
                  {
                    id: 'YdisplayInOe',
                    label: 'Yes',
                  },
                  {
                    id: 'NdisplayInOe',
                    label: 'No',
                  },
                ]}
                id='displayInOe'
                label='Valid in Order Entry?'
                layout={RadioGroupLayouts.Row}
                selected={formValues?.displayInOe ? 'YdisplayInOe' : 'NdisplayInOe'}
                onChange={handleDisplayInOeChange}
              />
            </RadioWrapper>
          </RowWrapper>
        </FormElements>
        <Buttons>
          <Button label='Save' onClick={handleSave} disabled={!unsavedChanges} />
        </Buttons>
      </Form>
      <SubTitle>{readOnlyLabelTableText}</SubTitle>
      <UnsavedChangesModal unsavedChangesExist={unsavedChanges} />
    </>
  );
};

export default EditBase;
